import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, Loader2, Pencil, Plus, Star, Trash2, UserRound } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import VoiceProfileDialog from "@/components/creator/voices/VoiceProfileDialog";
import VoicePreview from "@/components/creator/voices/VoicePreview";
import { deleteVoiceProfile, listVoiceProfiles, makeMainVoiceProfile } from "@/lib/studioApi";
import { safeApiError } from "@/lib/safeErrorText";

const pill = "inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white/70 transition-colors hover:text-white";

function ProfileRow({ p, onEdit, onMain, onDelete }) {
  return (
    <article data-testid={`voice-profile-${p.id}`} data-main={p.is_main ? "1" : "0"} className={`rounded-3xl border p-5 ${p.is_main ? "border-amber-300/40 bg-gradient-to-br from-amber-500/[0.1] via-rose-500/[0.05] to-transparent" : "border-white/[0.08] bg-[#0a0d1c]/80"}`}>
      <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-amber-200/90">
        {p.is_main ? <Crown className="h-3.5 w-3.5" /> : <UserRound className="h-3.5 w-3.5" />} {p.is_main ? "Main signature voice" : "Guest voice"}
        <span className="text-white/40">· {p.gender === "f" ? "Female" : "Male"}</span>
      </p>
      <h3 data-testid={`voice-profile-title-${p.id}`} className="mt-2 font-display text-lg font-bold">{p.name}</h3>
      <p className="mt-1.5 text-[13px] leading-relaxed text-white/60">{p.description || p.style}</p>
      <p className="mt-2 text-[11px] text-white/40">{p.cloned_voice_id ? "Recorded voice linked — used by “My Recorded Voice”." : "No recorded voice linked."}</p>
      {p.cloned_voice_id ? <VoicePreview key={`${p.cloned_voice_id}-${p.gender}`} profileId={p.id} initial={p.preview} /> : null}
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" data-testid={`voice-profile-edit-${p.id}`} onClick={() => onEdit(p)} className={pill}><Pencil className="h-3 w-3" /> Edit</button>
        {!p.is_main ? (
          <>
            <button type="button" data-testid={`voice-profile-main-${p.id}`} onClick={() => onMain(p)} className={pill}><Star className="h-3 w-3" /> Make main voice</button>
            <button type="button" data-testid={`voice-profile-delete-${p.id}`} onClick={() => onDelete(p)} className={`${pill} hover:text-rose-300`}><Trash2 className="h-3 w-3" /> Delete</button>
          </>
        ) : null}
      </div>
    </article>
  );
}

/** Signature voice profiles — one main voice (Apostle Moore) plus guest singers for duets. */
export default function VoiceProfilesPage() {
  const [profiles, setProfiles] = useState(null);
  const [dialog, setDialog] = useState({ open: false, profile: null });
  const load = useCallback(() => listVoiceProfiles().then(setProfiles).catch((e) => { toast.error("Couldn't load voice profiles", { description: safeApiError(e) }); setProfiles([]); }), []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); await load(); } catch (e) { toast.error("That didn't work", { description: safeApiError(e) }); }
  };
  const onDelete = (p) => { if (window.confirm(`Delete the voice profile "${p.name}"? Songs already made keep their audio.`)) act(() => deleteVoiceProfile(p.id), "Voice profile deleted"); };

  return (
    <CreatorShell testId="voice-profiles-page" back="/create" title="Voice profiles" subtitle="Your signature voice leads every song. Add guest voices for duets and features.">
      <div className="mb-6">
        <button type="button" data-testid="voice-profile-add" onClick={() => setDialog({ open: true, profile: null })} className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-6 py-3 font-display text-sm font-semibold transition-transform hover:scale-[1.02]">
          <Plus className="h-4 w-4" /> Add guest voice
        </button>
      </div>
      {profiles === null ? <p className="flex items-center gap-2 text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p> : (
        <div data-testid="voice-profiles-list" className="grid gap-4 lg:grid-cols-2">
          {profiles.map((p) => <ProfileRow key={p.id} p={p} onEdit={(x) => setDialog({ open: true, profile: x })} onMain={(x) => act(() => makeMainVoiceProfile(x.id), `${x.name} is now your main voice`)} onDelete={onDelete} />)}
        </div>
      )}
      <VoiceProfileDialog open={dialog.open} profile={dialog.profile} onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} onSaved={() => { setDialog({ open: false, profile: null }); load(); }} />
    </CreatorShell>
  );
}
